import { useState } from 'react'

interface Principle {
  id: string
  name: string
  description: string
  examples: string[]
  weight: 'required' | 'important' | 'nice-to-have'
  isActive: boolean
}

// Mock data - in production this comes from API
const MOCK_PRINCIPLES: Principle[] = [
  {
    id: '1',
    name: 'Customer Obsession',
    description: 'Leaders start with the customer and work backwards. They work vigorously to earn and keep customer trust.',
    examples: [
      'Demonstrates understanding of customer needs in past work',
      'Makes decisions with customer impact as primary consideration',
      'Has examples of going above and beyond for customers',
    ],
    weight: 'required',
    isActive: true,
  },
  {
    id: '2',
    name: 'Ownership',
    description: 'Leaders act on behalf of the entire company, beyond just their own team. They never say "that\'s not my job."',
    examples: [
      'Takes responsibility for outcomes, not just activities',
      'Thinks long-term and doesn\'t sacrifice long-term value for short-term results',
      'Owns problems until they are fully resolved',
    ],
    weight: 'required',
    isActive: true,
  },
  {
    id: '3',
    name: 'Bias for Action',
    description: 'Speed matters in business. Many decisions are reversible and do not need extensive study.',
    examples: [
      'Makes decisions quickly when appropriate',
      'Comfortable with calculated risk-taking',
      'Doesn\'t wait for perfect information before acting',
    ],
    weight: 'important',
    isActive: true,
  },
  {
    id: '4',
    name: 'Learn and Be Curious',
    description: 'Leaders are never done learning and always seek to improve themselves.',
    examples: [
      'Actively seeks out new knowledge and skills',
      'Asks thoughtful questions during interviews',
      'Shows growth trajectory in their career',
    ],
    weight: 'important',
    isActive: true,
  },
  {
    id: '5',
    name: 'Hire and Develop the Best',
    description: 'Leaders raise the performance bar with every hire and promotion.',
    examples: [
      'Has examples of mentoring or developing others',
      'Makes thoughtful hiring decisions when in a position to do so',
      'Gives candid feedback that helps others grow',
    ],
    weight: 'nice-to-have',
    isActive: false,
  },
]

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  )
}



function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function Bars3Icon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  )
}

function PrincipleCard({ 
  principle, 
  onEdit, 
  onToggle 
}: { 
  principle: Principle
  onEdit: () => void
  onToggle: () => void 
}) {
  const [expanded, setExpanded] = useState(false)

  const weightColors = {
    'required': 'bg-rose-50 text-rose-700 border-rose-200',
    'important': 'bg-amber-50 text-amber-700 border-amber-200',
    'nice-to-have': 'bg-surface-100 text-surface-600 border-surface-200',
  }

  const weightLabels = {
    'required': 'Required',
    'important': 'Important',
    'nice-to-have': 'Nice to have',
  }

  return (
    <div className={`border rounded-xl transition-all ${
      principle.isActive 
        ? 'border-surface-200 bg-white' 
        : 'border-surface-100 bg-surface-50 opacity-60'
    }`}>
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          {/* Drag handle */}
          <button className="mt-1 p-1 text-surface-400 hover:text-surface-600 cursor-grab">
            <Bars3Icon className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-surface-900">{principle.name}</h3>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${weightColors[principle.weight]}`}>
                    {weightLabels[principle.weight]}
                  </span>
                </div>
                <p className="mt-1 text-sm text-surface-600 line-clamp-2">
                  {principle.description}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={onEdit}
                  className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
                  title="Edit principle"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
                  title={expanded ? 'Collapse' : 'Expand'}
                >
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {/* Expanded content */}
            {expanded && (
              <div className="mt-4 pt-4 border-t border-surface-100">
                <p className="text-sm font-medium text-surface-700 mb-2">What to look for:</p>
                <ul className="space-y-1.5">
                  {principle.examples.map((example, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-surface-600">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-surface-400 shrink-0" />
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Toggle */}
          <button
            onClick={onToggle}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              principle.isActive ? 'bg-brand-600' : 'bg-surface-200'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
              principle.isActive ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function HiringPrinciplesSettings() {
  const [principles, setPrinciples] = useState(MOCK_PRINCIPLES)
  const [, setShowAddModal] = useState(false)

  const handleToggle = (id: string) => {
    setPrinciples(prev => prev.map(p => 
      p.id === id ? { ...p, isActive: !p.isActive } : p
    ))
  }

  const activePrinciples = principles.filter(p => p.isActive)
  const inactivePrinciples = principles.filter(p => !p.isActive)

  return (
    <div className="divide-y divide-surface-100">
      {/* Header */}
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">Hiring Principles</h2>
            <p className="mt-1 text-surface-600">
              Define the values and behaviors that every candidate should demonstrate. These principles are referenced during evaluations.
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Add principle
          </button>
        </div>
      </div>

      {/* Info callout */}
      <div className="p-6 sm:p-8 bg-brand-50/50">
        <div className="flex gap-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-brand-700" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-brand-900">How principles are used</h3>
            <p className="mt-1 text-sm text-brand-700">
              Evaluators will see these principles during candidate assessments. They provide a structured framework for consistent, fair evaluation across all interviewers and roles. Candidates may also see principles in rejection feedback if enabled.
            </p>
          </div>
        </div>
      </div>

      {/* Active principles */}
      <div className="p-6 sm:p-8">
        <h3 className="text-sm font-medium text-surface-900 mb-4">
          Active principles ({activePrinciples.length})
        </h3>
        <div className="space-y-3">
          {activePrinciples.map((principle) => (
            <PrincipleCard
              key={principle.id}
              principle={principle}
              onEdit={() => {}}
              onToggle={() => handleToggle(principle.id)}
            />
          ))}
        </div>
      </div>

      {/* Inactive principles */}
      {inactivePrinciples.length > 0 && (
        <div className="p-6 sm:p-8">
          <h3 className="text-sm font-medium text-surface-500 mb-4">
            Inactive principles ({inactivePrinciples.length})
          </h3>
          <div className="space-y-3">
            {inactivePrinciples.map((principle) => (
              <PrincipleCard
                key={principle.id}
                principle={principle}
                onEdit={() => {}}
                onToggle={() => handleToggle(principle.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {principles.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-surface-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
            </svg>
          </div>
          <h3 className="font-medium text-surface-900 mb-1">No principles defined</h3>
          <p className="text-surface-500 text-sm mb-6">
            Add your first hiring principle to create a consistent evaluation framework.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add first principle
          </button>
        </div>
      )}
    </div>
  )
}

